import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchStudentPayments, fetchMyPaymentDetails } from '../../store/slices/paymentSlice';
import { LessonStatus, PaymentStatus } from '../../types/portal';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { apiService } from '../../services/api';
import { formatAmsterdamDate } from '../../utils/formatAmsterdamDate';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LessonCard from '../../components/portal/LessonCard';

const DashboardWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
  position: relative;
  color: #111827;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  padding-top: 40px;

  @media (min-width: 1241px) {
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
    padding-top: 32px;
  }
`;

const WelcomeSection = styled.div`
  margin-bottom: 2.5rem;
  text-align: left;
  h1 {
    font-size: var(--font-size-h1);
    font-weight: 800;
    color: var(--text-main);
    margin-bottom: 0.5rem;
  }
  p {
    color: #4b5563;
    font-size: var(--font-size-p);
  }

  @media (max-width: 768px) {
    text-align: center;
    h1 {
      font-size: var(--font-size-h2);
    }
    p {
      font-size: var(--font-size-p);
    }
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const AnimatedCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  padding: 1.5rem;
  height: 100%;
`;

const NextLessonCard = styled(AnimatedCard)`
  background: linear-gradient(135deg, var(--primary-orange) 0%, var(--color-sky) 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin-bottom: 2rem;

  .label {
    text-transform: uppercase;
    font-size: var(--font-size-small);
    font-weight: 700;
    opacity: 0.9;
    letter-spacing: 0.05em;
    margin-bottom: 0.5rem;
  }

  .time {
    font-size: var(--font-size-h5);
    font-weight: 800;
  }

  .location {
    font-size: var(--font-size-p);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    opacity: 0.9;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h3 {
    font-size: var(--font-size-h5);
    font-weight: 700;
    color: var(--text-main);
    margin: 0;

    @media (max-width: 768px) {
      font-size: var(--font-size-h6);
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ListItem = styled(motion.div)`
  padding: 0.6rem 0.85rem;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px solid rgba(0, 0, 0, 0.03);
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    transform: translateX(4px);
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ActionButton = styled(motion.button)<{ variant?: 'primary' | 'outline', size?: 'sm' }>`
  padding: ${props => props.size === 'sm' ? '0.4rem 0.8rem' : '0.75rem 1.5rem'};
  border-radius: ${props => props.size === 'sm' ? '10px' : '14px'};
  font-weight: 700;
  font-size: ${props => props.size === 'sm' ? '0.8rem' : '0.9rem'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'outline' 
    ? 'background: transparent; border: 2px solid #e5e7eb; color: #374151;' 
    : 'background: var(--primary-orange); color: white;'}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
`;

const Badge = styled.span<{ type?: 'blue' | 'green' | 'orange' | 'red' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  
  ${props => {
    switch (props.type) {
      case 'green': return 'background: #dcfce7; color: #065f46;';
      case 'orange': return 'background: #ffedd5; color: #9a3412;';
      case 'red': return 'background: #fee2e2; color: #991b1b;';
      default: return 'background: #dbeafe; color: #1e40af;';
    }
  }}
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

// Robust nl-NL currency formatter with manual fallback for limited ICU environments
const formatCurrencyNL = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(amount);
  } catch {
    const sign = amount < 0 ? '-' : '';
    const n = Math.abs(amount);
    const fixed = n.toFixed(2);
    const [intsRaw, dec = '00'] = fixed.split('.');
    const ints = intsRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const symbol = currency === 'EUR' ? '€' : currency;
    return `${sign}${symbol} ${ints},${dec}`;
  }
};

const StudentDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { studentPayments, paymentDetails, isLoading: paymentsLoading } = useAppSelector((state) => state.payment);
  const navigate = useNavigate();

  const [myLessons, setMyLessons] = React.useState<any[]>([]);
  const [availableLessons, setAvailableLessons] = React.useState<any[]>([]);
  const [myExams, setMyExams] = React.useState<any[]>([]);
  const [lessonsLoading, setLessonsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [booking, setBooking] = React.useState<string | number | null>(null);
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);
  const [selectedAvailDate, setSelectedAvailDate] = React.useState<string>('');
  // Billing (current balance)
  const [bill, setBill] = React.useState<{ amount: number; currency: string } | null>(null);
  const [billLoading, setBillLoading] = React.useState<boolean>(false);
  const [billError, setBillError] = React.useState<string | null>(null);

  const [stats, setStats] = React.useState<{ completedCount: number; upcomingCount: number; averageTeacherGrade: number | null }>({ completedCount: 0, upcomingCount: 0, averageTeacherGrade: 0 });

  const loadStats = async () => {
    try {
      const resp = await apiService.get<any>(API_ENDPOINTS.LESSONS.STATISTICS_STUDENT);
      if (resp?.data) setStats(resp.data);
    } catch (e) {}
  };

  const loadExams = async () => {
    try {
      const resp = await apiService.get<any[]>(API_ENDPOINTS.EXAMS.STUDENT_MY);
      if (Array.isArray(resp)) setMyExams(resp.slice(0, 3));
    } catch (e) {}
  };

  const bookLesson = async (lessonId: string | number) => {
    try {
      setBooking(lessonId);
      setError(null);
      const resp = await apiService.post<any>(API_ENDPOINTS.LESSONS.BOOK(lessonId));
      setSuccessMsg(resp?.message || 'Lesson booked successfully');
      
      // Auto-download ICS if iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isIOS) {
        window.location.href = `${apiService.getBaseUrl()}/api/lessons/${lessonId}/calendar/ics`;
      }

      setTimeout(() => setSuccessMsg(null), 3000);
      await refreshLessons();
      loadStats();
    } catch (e: any) {
      setError(e?.message || 'Failed to book lesson');
    } finally {
      setBooking(null);
    }
  };

  const refreshLessons = React.useCallback(async () => {
    setLessonsLoading(true);
    setError(null);
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      // Fetch my lessons (booked by this student)
      const my = await apiService.get<any>(`/api/lessons/my-lessons?from=${encodeURIComponent(todayStr + 'T00:00:00')}`);
      const myList = Array.isArray(my?.data) ? my.data : Array.isArray(my) ? my : [];

      // Fetch available lessons
      let avListRaw: any[] = [];
      if (selectedAvailDate) {
        const startDate = `${selectedAvailDate}T00:00:00`;
        const endDate = `${selectedAvailDate}T23:59:59`;
        const av = await apiService.get<any>(`${API_ENDPOINTS.LESSONS.MY_AVAILABLE}?from=${encodeURIComponent(startDate)}&to=${encodeURIComponent(endDate)}`);
        avListRaw = Array.isArray(av?.data) ? av.data : Array.isArray(av) ? av : [];
      } else {
        const av = await apiService.get<any>(API_ENDPOINTS.LESSONS.MY_AVAILABLE);
        avListRaw = Array.isArray(av?.data) ? av.data : Array.isArray(av) ? av : [];
      }

      // Normalize items minimally to match Lesson typing used in UI
      const mapLesson = (l: any) => ({
        id: String(l.id ?? l.lessonId ?? ''),
        studentId: l.student?.id ?? l.studentId ?? null,
        teacherId: l.teacher?.id ?? l.teacherId ?? null,
        teacherName: (() => {
          const t = l.teacher;
          if (t) {
            const f = (t.firstName ?? '').trim();
            const ls = (t.lastName ?? '').trim();
            if (!f || !ls) return (t.username ?? `${f} ${ls}`.trim());
            return `${f} ${ls}`.trim();
          }
          return l.teacherName ?? undefined;
        })(),
        dateTime: l.lessonDateTime ?? l.dateTime,
        duration: l.durationMinutes ?? l.duration ?? 60,
        status: (typeof l.isBooked === 'boolean') ? (l.isBooked ? 'scheduled' : 'available') : (l.status ?? 'scheduled'),
        type: l.lessonType ?? l.type ?? 'REGULAR',
        // derive location: student's street if assigned, else Amsterdam
        location: (() => {
          const addr = l.student?.address ?? null;
          if (addr && typeof addr === 'string') {
            const street = addr.split(',')[0].trim();
            return street || 'Amsterdam';
          }
          return l.location ?? 'Amsterdam';
        })(),
        pickupLocation: l.pickupLocation ?? undefined,
        // Notes fields (new server fields with legacy fallback)
        notes: l.notes ?? undefined,
        teacherNote: l.teacherNote ?? undefined,
        studentNote: l.studentNote ?? undefined,
        studentName: (() => {
          const s = l.student;
          if (s) {
            const f = (s.firstName ?? '').trim();
            const ls = (s.lastName ?? '').trim();
            if (!f || !ls) return (s.username ?? `${f} ${ls}`.trim());
            return `${f} ${ls}`.trim();
          }
          return l.studentName ?? l.student?.studentName ?? undefined;
        })(),
        createdAt: l.createdAt,
        updatedAt: l.updatedAt,
      });

      const myMapped = myList.map(mapLesson);
      const avMapped = avListRaw.map(mapLesson);
      const ascend = (a: any, b: any) => {
        const ta = a?.dateTime ? Date.parse(a.dateTime) : Number.MAX_SAFE_INTEGER;
        const tb = b?.dateTime ? Date.parse(b.dateTime) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      };
      setMyLessons([...myMapped].sort(ascend));
      setAvailableLessons([...avMapped].sort(ascend));
    } catch (e: any) {
      setError(e?.message || 'Failed to load lessons');
    } finally {
      setLessonsLoading(false);
    }
  }, [selectedAvailDate]);

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchStudentPayments(String(user.id)));
      dispatch(fetchMyPaymentDetails());
      refreshLessons();
      loadStats();
      loadExams();
      // Load current balance
      (async () => {
        try {
          setBillLoading(true); setBillError(null);
          const resp = await apiService.get<any>(API_ENDPOINTS.BILLING.MY_BILL);
          const data = (resp && typeof resp === 'object' && 'data' in resp) ? (resp as any).data : resp;
          const amt = Number(data?.amount ?? 0);
          const cur = String(data?.currency ?? 'EUR');
          setBill({ amount: amt, currency: cur });
        } catch (e: any) {
          setBillError(e?.message || 'Failed to load balance');
        } finally { setBillLoading(false); }
      })();
    }
  }, [dispatch, user?.id, refreshLessons]);

  if (!user) {
    return null;
  }

  // Filter lessons
  const now = new Date();
  const upcomingLessons = myLessons
    .filter(lesson => new Date(lesson.dateTime) > now)
    .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
  const nextLesson = upcomingLessons[0] || null;
  const currentLessons = myLessons.filter(lesson => {
    const start = new Date(lesson.dateTime).getTime();
    const end = start + (Number(lesson.duration || 60) * 60000);
    const t = now.getTime();
    return t >= start && t < end;
  });

  const pastLessons = myLessons.filter(lesson => new Date(lesson.dateTime) < now);

  const pendingPayments = studentPayments.filter(payment => 
    payment.status === PaymentStatus.PENDING
  );

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case LessonStatus.SCHEDULED:
        return 'bg-blue-100 text-blue-800';
      case LessonStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case LessonStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      case PaymentStatus.VERIFIED:
        return 'bg-green-100 text-green-800';
      case PaymentStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case PaymentStatus.REJECTED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Tip of the Day state
  const [tipOfDay, setTipOfDay] = React.useState<any | null>(null);
  const [tipLoading, setTipLoading] = React.useState<boolean>(false);
  const [tipError, setTipError] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setTipLoading(true); setTipError(null);
        const resp = await apiService.get<any>('/api/tips/today');
        const data = (resp && typeof resp === 'object' && 'data' in resp) ? (resp as any).data : resp;
        if (!cancelled) setTipOfDay(data);
      } catch (e: any) {
        if (!cancelled) setTipError(e?.message || 'Failed to load tip');
      } finally { if (!cancelled) setTipLoading(false); }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <DashboardWrapper>
      <ContentWrapper>
        {successMsg && (
          <div style={{ position: 'fixed', top: 90, right: 24, background: '#10b981', color: 'white', padding: '0.6rem 0.9rem', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', zIndex: 3000, fontWeight: 700 }}>{successMsg}</div>
        )}
        {error && (
          <div style={{ position: 'fixed', top: 90, right: 24, background: '#ef4444', color: 'white', padding: '0.6rem 0.9rem', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', zIndex: 3000, fontWeight: 700 }}>{error}</div>
        )}

        <WelcomeSection>
          <h2>{t('portal.student.dashboard.welcome', 'Hello, {{name}}!', { name: user.firstName || user.username })}</h2>
        </WelcomeSection>


        {nextLesson && (
          <NextLessonCard
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.01 }}
          >
            <div>
              <div className="label">{t('portal.student.dashboard.next_lesson_label', 'Next Lesson')}</div>
              <div className="time">
                {new Date(nextLesson.dateTime).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} {t('portal.student.dashboard.next_lesson_at', 'at')} {new Date(nextLesson.dateTime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="location">
                {nextLesson.pickupLocation || nextLesson.location || t('portal.student.dashboard.meeting_point', 'Meeting point')} • {nextLesson.duration} {t('portal.student.dashboard.minutes_short', 'min')}
              </div>
            </div>
            <ActionButton variant="outline" style={{ background: 'white', color: 'var(--primary-orange)', border: 'none' }} onClick={() => navigate(`/portal/lesson/${nextLesson.id}`)}>
              {t('portal.common.details', 'Details')}
            </ActionButton>
          </NextLessonCard>
        )}

        <MainGrid>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <AnimatedCard>
              <SectionHeader>
                <h3>{t('portal.student.dashboard.my_lessons_title', 'My Lessons')}</h3>
                <ActionButton variant="outline" onClick={() => navigate('/portal/lesson')}>{t('portal.common.view_all', 'View All')}</ActionButton>
              </SectionHeader>
              {lessonsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>{t('portal.common.loading', 'Loading…')}</div>
              ) : myLessons.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>{t('portal.student.dashboard.no_lessons', 'No lessons scheduled.')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {myLessons.slice(0, 5).map((l) => {
                    const isPassed = new Date(l.dateTime) < new Date();
                    return (
                      <LessonCard
                        key={l.id}
                        lesson={l}
                        mode={isPassed ? 'passed' : 'booked'}
                        onClick={() => navigate(`/portal/lesson/${l.id}`)}
                      />
                    );
                  })}
                </div>
              )}
            </AnimatedCard>

            {myExams.length > 0 && (
              <AnimatedCard>
                <SectionHeader>
                  <h3>{t('portal.student.dashboard.my_exams_title', 'My Exams')}</h3>
                  <ActionButton variant="outline" onClick={() => navigate('/portal/student/exams')}>{t('portal.common.view_all', 'View All')}</ActionButton>
                </SectionHeader>
                <ListContainer>
                  {myExams.map((ex) => (
                    <ListItem key={ex.id} onClick={() => navigate(`/portal/student/exams/${ex.id}`)}>
                      <div className="main-info">
                        <div style={{ fontWeight: 700 }}>{ex.type || 'EXAM'} - {ex.licenseType || ''}</div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {formatAmsterdamDate(ex.startTime || ex.examDate)} • {ex.location || 'No location set'}
                        </div>
                      </div>
                      <Badge type={ex.result === true ? 'green' : (ex.result === false ? 'red' : (new Date(ex.startTime || ex.examDate) > new Date() ? 'orange' : 'orange'))}>
                        {ex.result === true ? 'PASSED' : (ex.result === false ? 'FAILED' : (new Date(ex.startTime || ex.examDate) > new Date() ? 'UPCOMING' : 'PENDING'))}
                      </Badge>
                    </ListItem>
                  ))}
                </ListContainer>
              </AnimatedCard>
            )}

            <AnimatedCard>
              <SectionHeader>
                <h3>{t('portal.student.dashboard.available_lessons_title', '')}</h3>
                <ActionButton variant="outline" onClick={() => navigate('/portal/lesson#available')}>{t('portal.student.dashboard.see_more', 'See more')}</ActionButton>
              </SectionHeader>
              {lessonsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>{t('portal.common.loading', 'Loading…')}</div>
              ) : availableLessons.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>{t('portal.student.dashboard.no_available_lessons', 'No lessons available right now.')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {availableLessons.slice(0, 5).map((l) => (
                    <LessonCard
                      key={l.id}
                      lesson={l}
                      mode="available"
                      onBook={bookLesson}
                      isBooking={booking === l.id}
                    />
                  ))}
                </div>
              )}
            </AnimatedCard>

            <AnimatedCard>
              <SectionHeader>
                <h3>{t('portal.student.dashboard.teacher_feedback_title', "Teacher's Feedback")}</h3>
              </SectionHeader>
              {(() => {
                const notes = myLessons
                  .map((l) => ({
                    ...l,
                    noteText: (typeof l.teacherNote === 'string' && l.teacherNote.trim().length > 0)
                      ? l.teacherNote
                      : (typeof l.notes === 'string' ? l.notes : '')
                  }))
                  .filter((l) => typeof l.noteText === 'string' && l.noteText.trim().length > 0)
                  .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
                
                if (notes.length === 0) return <div style={{ padding: '1rem', color: '#6b7280', textAlign: 'center' }}>{t('portal.student.dashboard.no_feedback', 'No feedback yet. Keep going!')}</div>;
                
                return notes.slice(0, 3).map((n) => (
                  <ListItem key={n.id}>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{formatAmsterdamDate(n.dateTime)}</div>
                        <div style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: '0.25rem' }}>{n.noteText}</div>
                      </div>
                    </div>
                  </ListItem>
                ));
              })()}
            </AnimatedCard>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <AnimatedCard>
              <SectionHeader>
                <h3>{t('portal.student.dashboard.recent_payments_title', 'Payment Details')}</h3>
              </SectionHeader>
              {paymentsLoading ? (
                <div>{t('portal.common.loading', 'Loading…')}</div>
              ) : !paymentDetails ? (
                <div style={{ color: '#6b7280', textAlign: 'center' }}>{t('portal.student.dashboard.no_payments', 'No payment details available.')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div style={{ background: '#f0fdf4', padding: '0.75rem', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.7rem', color: '#166534', fontWeight: 700, textTransform: 'uppercase' }}>Paid</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#166534' }}>{formatCurrencyNL(paymentDetails.totalPaid, paymentDetails.currency)}</div>
                    </div>
                    <div style={{ background: '#fff7ed', padding: '0.75rem', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.7rem', color: '#9a3412', fontWeight: 700, textTransform: 'uppercase' }}>Pending</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#9a3412' }}>{formatCurrencyNL(paymentDetails.totalPending, paymentDetails.currency)}</div>
                    </div>
                  </div>
                  
                  {paymentDetails.totalDue > 0 && (
                    <div style={{ background: '#fef2f2', padding: '0.75rem', borderRadius: '12px', textAlign: 'center', border: '1px solid #fee2e2' }}>
                      <div style={{ fontSize: '0.7rem', color: '#991b1b', fontWeight: 700, textTransform: 'uppercase' }}>Outstanding Balance</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#991b1b' }}>{formatCurrencyNL(paymentDetails.totalDue, paymentDetails.currency)}</div>
                    </div>
                  )}

                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.5rem', color: '#374151' }}>Breakdown</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                        <span>Lessons:</span>
                        <span style={{ fontWeight: 600 }}>{(paymentDetails.lessons || []).length}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                        <span>Exams:</span>
                        <span style={{ fontWeight: 600 }}>{(paymentDetails.exams || []).length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <ActionButton variant="outline" style={{ width: '100%', marginTop: '1.5rem' }} onClick={() => navigate('/portal/student/agenda')}>
                {t('nav.agenda', 'My Agenda')}
              </ActionButton>
            </AnimatedCard>

            <AnimatedCard>
              <SectionHeader>
                <h3>{t('portal.student.dashboard.quick_tip_title', 'Quick Tip')}</h3>
              </SectionHeader>
              {tipLoading ? (
                <div>...</div>
              ) : tipOfDay?.content ? (
                <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '16px', border: '1px solid #bae6fd', color: '#0369a1', fontSize: '0.9rem', lineHeight: '1.5' }}>
                  <strong>💡 {t('portal.student.dashboard.tip_label', 'Tip')}:</strong> {tipOfDay.content}
                </div>
              ) : (
                <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>{t('portal.student.dashboard.default_tip', 'Always check your mirrors before changing lanes!')}</div>
              )}
            </AnimatedCard>

            <AnimatedCard>
              <SectionHeader>
                <h3>{t('portal.student.dashboard.resources_title', 'Resources')}</h3>
              </SectionHeader>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <ActionButton variant="outline" style={{ justifyContent: 'center' }}>📘 {t('portal.student.dashboard.theory_material', 'Theory Material')}</ActionButton>
                <ActionButton variant="outline" style={{ justifyContent: 'center' }}>🆘 {t('portal.student.dashboard.help_center', 'Help Center')}</ActionButton>
              </div>
            </AnimatedCard>
          </div>
        </MainGrid>
      </ContentWrapper>
    </DashboardWrapper>
  );
};

export default StudentDashboard;